/**
 * 💡 LD TOOLBOX — DMX & Light (DIP-switch, patch, gélatines, faisceau, triphasé).
 *
 * Pourquoi ce fichier : ce sont les cinq calculs qu'un éclairagiste refait à
 * chaque montage, souvent de tête, souvent faux. Les avoir dans le téléphone —
 * hors-ligne, dans le même onglet que le reste du « stage toolbox » — évite
 * l'erreur qui coûte une univers DMX ou une prise qui disjoncte.
 *
 * RÈGLES (elles tiennent tout le fichier) :
 *  - ZÉRO OCTET SUR LE SERVEUR, ZÉRO dépendance : ce sont des maths pures et
 *    du DOM. Rien à installer sur un coin de scène.
 *  - Les fonctions de calcul sont EXPORTÉES et pures (`dmxToDipSwitches`,
 *    `dipSwitchesToDmx`, `computeBeamDiameter`, `computeThreePhaseLoad`) :
 *    c'est la seule partie vérifiable sans navigateur, donc elle est testée.
 *  - Aucune donnée de groupe, aucune valeur d'artiste codée en dur : les
 *    gélatines sont un catalogue standard (Lee/Rosco), pas une prescription.
 *  - i18n 100 % anglais (`games.ld.*`).
 *
 * RÉFÉRENCE DU LOT : `bandz-frontend/references/ui-roadcase-redesign-2026.md`
 * (section LOT STAGE-TOOLBOX-AND-ARCADE-PLUS).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clamp } from './gameKit';

/* ====================================================================== */
/*  DMX — DIP switches (9 bits, 1..512)                                   */
/* ====================================================================== */

/** Poids binaires des 9 micro-interrupteurs d'un décodeur DMX. */
export const DIP_WEIGHTS: readonly number[] = [1, 2, 4, 8, 16, 32, 64, 128, 256];

/**
 * Adresse DMX → état des 9 switches.
 *
 * 9 micro-interrupteurs codent les poids 1..256, donc la SOMME maximale est
 * 511 : l'adresse 512 n'est pas représentable sur un décodeur 9 bits (les
 * décodeurs récents ajoutent un 10e switch, ou l'obtiennent par adresse + 1).
 * On borne donc à 1..511 plutôt que d'afficher une combinaison fausse.
 */
export function dmxToDipSwitches(address: number): boolean[] {
  const value = clamp(Math.round(address), 0, 511);
  return DIP_WEIGHTS.map((weight) => (value & weight) !== 0);
}

/** Somme des poids allumés → adresse DMX (0 si tout est éteint). */
export function dipSwitchesToDmx(switches: boolean[]): number {
  return DIP_WEIGHTS.reduce((sum, weight, i) => (switches[i] ? sum + weight : sum), 0);
}

/** Les poids allumés, pour l'affichage compact `DIP: 1, 16`. */
export function dipLabels(address: number): number[] {
  return DIP_WEIGHTS.filter((weight) => (clamp(Math.round(address), 0, 511) & weight) !== 0);
}

/* ====================================================================== */
/*  Faisceau — diamètre du spot au sol                                    */
/* ====================================================================== */

/**
 * Ø = 2 × d × tan(θ/2). C'est la formule de la nappe conique : à 10 m avec un
 * 26°, le spot fait 4,62 m — l'écart entre « ça couvre la scène » et « ça
 * éclaire la première rangée ».
 */
export function computeBeamDiameter(distanceM: number, angleDeg: number): number {
  if (!Number.isFinite(distanceM) || !Number.isFinite(angleDeg)) return 0;
  const d = Math.max(0, distanceM);
  const angle = clamp(angleDeg, 0.1, 179) * (Math.PI / 180);
  return 2 * d * Math.tan(angle / 2);
}

/* ====================================================================== */
/*  Puissance — P = U × I, et répartition triphasée                       */
/* ====================================================================== */

export interface ThreePhaseLoad {
  /** Courant total absorbé (A) — P / (U × cos φ). */
  totalAmps: number;
  /** Courant par phase si la charge est équilibrée (A) — I / 3. */
  perPhase: number;
  /** Puissance apparente (kVA). */
  kva: number;
  /** Prise à demander, du plus petit au plus gros. */
  connector: string;
}

/**
 * Charge en watts + tension → courant total, courant par phase et prise.
 *
 * Pourquoi /3 et pas /√3 : sur une distribution triphasée ÉQUILIBRÉE, chaque
 * phase porte un tiers de la charge totale (le √3 n'apparaît que si l'on part
 * de la tension composée). C'est le calcul de terrain : 18 kW en 230 V
 * monophasé = 78 A, soit ~26 A par phase → une 32 A triphasée.
 */
export function computeThreePhaseLoad(watts: number, voltage: number, powerFactor = 1): ThreePhaseLoad {
  const p = Math.max(0, Number.isFinite(watts) ? watts : 0);
  const u = Math.max(1, Number.isFinite(voltage) ? voltage : 230);
  const pf = clamp(Number.isFinite(powerFactor) ? powerFactor : 1, 0.1, 1);
  const totalAmps = p / (u * pf);
  const perPhase = totalAmps / 3;
  const kva = (p / 1000) / pf;

  let connector = '16A Mono';
  if (perPhase > 125) connector = '125A Powerlock ×2';
  else if (perPhase > 63) connector = '125A Powerlock';
  else if (perPhase > 32) connector = '63A 3P CEE';
  else if (perPhase > 16) connector = '32A 3P CEE';
  else if (perPhase > 10) connector = '16A 3P CEE';

  return { totalAmps, perPhase, kva, connector };
}

/* ====================================================================== */
/*  Gélatines Lee / Rosco — catalogue de scène                            */
/* ====================================================================== */

export interface Gel {
  /** Code fabricant, la clé de recherche (`L201`, `R80`…). */
  code: string;
  name: string;
  hex: string;
  /** Température de couleur approximative une fois la gélatine en place. */
  kelvin: number;
}

/** 19 gélatines qu'on trouve réellement dans une valise de scène. */
export const GELS: readonly Gel[] = [
  { code: 'L201', name: 'Full CTB', hex: '#7fb2e5', kelvin: 12000 },
  { code: 'L202', name: 'Half CTB', hex: '#9ec7ee', kelvin: 7200 },
  { code: 'L204', name: 'Full CTO', hex: '#ff9d2e', kelvin: 3200 },
  { code: 'L205', name: 'Half CTO', hex: '#ffb860', kelvin: 3800 },
  { code: 'L206', name: 'Quarter CTO', hex: '#ffcc90', kelvin: 4300 },
  { code: 'L106', name: 'Primary Red', hex: '#ff0000', kelvin: 2000 },
  { code: 'L164', name: 'Flame Red', hex: '#ff3b1f', kelvin: 2100 },
  { code: 'L158', name: 'Deep Orange', hex: '#ff6a00', kelvin: 2300 },
  { code: 'L135', name: 'Deep Golden Amber', hex: '#ffa000', kelvin: 2600 },
  { code: 'L778', name: 'Millennium Gold', hex: '#ffc63d', kelvin: 2900 },
  { code: 'L101', name: 'Yellow', hex: '#ffe600', kelvin: 3000 },
  { code: 'L139', name: 'Primary Green', hex: '#00b33c', kelvin: 5500 },
  { code: 'L124', name: 'Dark Green', hex: '#005c2e', kelvin: 5200 },
  { code: 'L119', name: 'Dark Blue', hex: '#1a2f7a', kelvin: 9000 },
  { code: 'L079', name: 'Just Blue', hex: '#0057ff', kelvin: 8500 },
  { code: 'L181', name: 'Congo Blue', hex: '#14246b', kelvin: 10000 },
  { code: 'L126', name: 'Mauve', hex: '#8e4a9e', kelvin: 6000 },
  { code: 'L136', name: 'Pale Lavender', hex: '#d3b6e8', kelvin: 6500 },
  { code: 'R80', name: 'Primary Blue', hex: '#0044cc', kelvin: 8800 },
];

export interface GelCodes {
  rgb: string;
  cmy: string;
  hex: string;
  kelvin: number;
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

/** Couleur d'une gélatine → ses codes console (RGB, CMY %, Hex, Kelvin). */
export function gelCodes(gel: Gel): GelCodes {
  const [r, g, b] = hexToRgb(gel.hex);
  const c = Math.round((1 - r / 255) * 100);
  const m = Math.round((1 - g / 255) * 100);
  const y = Math.round((1 - b / 255) * 100);
  return {
    rgb: `${r}, ${g}, ${b}`,
    cmy: `${c}%, ${m}%, ${y}%`,
    hex: gel.hex.toUpperCase(),
    kelvin: gel.kelvin,
  };
}

/* ====================================================================== */
/*  Panneau                                                               */
/* ====================================================================== */

export function LdToolboxPanel() {
  const { t } = useTranslation();

  /* ---- DIP switch ---- */
  const [address, setAddress] = useState(1);
  const [switches, setSwitches] = useState<boolean[]>(() => dmxToDipSwitches(1));

  const applyAddress = (next: number) => {
    const value = clamp(Math.round(next), 0, 511);
    setAddress(value);
    setSwitches(dmxToDipSwitches(value));
  };

  const toggleSwitch = (index: number) => {
    const next = switches.map((on, i) => (i === index ? !on : on));
    setSwitches(next);
    setAddress(dipSwitchesToDmx(next));
  };

  /* ---- Patch stepper ---- */
  const [start, setStart] = useState(1);
  const [channels, setChannels] = useState(16);
  const [count, setCount] = useState(12);

  const patch = useMemo(() => {
    const rows: { index: number; address: number; dip: number[]; overflow: boolean }[] = [];
    for (let i = 0; i < clamp(Math.round(count), 1, 64); i += 1) {
      const addr = clamp(Math.round(start), 1, 512) + i * clamp(Math.round(channels), 1, 128);
      rows.push({
        index: i + 1,
        address: addr,
        dip: dipLabels(addr),
        // Dépassement : le dernier canal de la machine sort de l'univers 1.
        overflow: addr + clamp(Math.round(channels), 1, 128) - 1 > 512,
      });
    }
    return rows;
  }, [channels, count, start]);

  /* ---- Gélatines ---- */
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Gel>(GELS[0]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GELS;
    return GELS.filter(
      (gel) =>
        gel.code.toLowerCase().includes(q) ||
        gel.name.toLowerCase().includes(q) ||
        `${gel.code} ${gel.name}`.toLowerCase().includes(q),
    );
  }, [query]);

  const codes = gelCodes(selected);

  /* ---- Faisceau ---- */
  const [distance, setDistance] = useState(10);
  const [angle, setAngle] = useState(26);
  const diameter = computeBeamDiameter(distance, angle);
  // Éclairement relatif : une source qui éclaire 2× plus loin éclaire 4× moins.
  const throwRatio = distance > 0 ? 1 / (distance * distance) : 0;

  /* ---- Puissance ---- */
  const [watts, setWatts] = useState(18000);
  const [voltage, setVoltage] = useState(230);
  const [pf, setPf] = useState(1);
  const load = computeThreePhaseLoad(watts, voltage, pf);

  return (
    <section className="game-panel tool-panel ld-panel" data-game="ld" data-game-panel="ld">
      {/* ------------------------------------------------ DIP switch */}
      <div className="tool-card">
        <div className="tool-card-head">{t('games.ld.dipTitle')}</div>

        <div className="tool-row">
          <label className="tool-field">
            <span>{t('games.ld.dmxAddress')}</span>
            <input
              type="number"
              min={1}
              max={511}
              value={address}
              data-ld-address
              onChange={(event) => applyAddress(Number(event.target.value))}
            />
          </label>
          <div className="ld-address-value">
            <b data-ld-address-value>{address}</b>
            <span>DMX</span>
          </div>
        </div>

        <div className="dip-grid" role="group" aria-label={t('games.ld.dipTitle')}>
          {DIP_WEIGHTS.map((weight, i) => (
            <button
              key={weight}
              type="button"
              className={`dip-switch${switches[i] ? ' on' : ''}`}
              data-dip-switch={weight}
              aria-pressed={switches[i]}
              onClick={() => toggleSwitch(i)}
            >
              <span className="dip-num">{weight}</span>
              <span className="dip-state">{switches[i] ? 'ON' : 'OFF'}</span>
            </button>
          ))}
        </div>

        <p className="tool-hint">{t('games.ld.dipHint')}</p>
      </div>

      {/* ------------------------------------------------ Patch stepper */}
      <div className="tool-card">
        <div className="tool-card-head">{t('games.ld.patchTitle')}</div>

        <div className="tool-row">
          <label className="tool-field">
            <span>{t('games.ld.startAddress')}</span>
            <input
              type="number"
              min={1}
              max={512}
              value={start}
              data-ld-patch-start
              onChange={(event) => setStart(Number(event.target.value))}
            />
          </label>
          <label className="tool-field">
            <span>{t('games.ld.channels')}</span>
            <input
              type="number"
              min={1}
              max={128}
              value={channels}
              data-ld-patch-channels
              onChange={(event) => setChannels(Number(event.target.value))}
            />
          </label>
          <label className="tool-field">
            <span>{t('games.ld.fixtures')}</span>
            <input
              type="number"
              min={1}
              max={64}
              value={count}
              data-ld-patch-count
              onChange={(event) => setCount(Number(event.target.value))}
            />
          </label>
        </div>

        <ol className="patch-list" data-ld-patch-list>
          {patch.map((row) => (
            <li key={row.index} className={row.overflow ? 'overflow' : ''}>
              <span className="patch-index">#{row.index}</span>
              <span className="patch-addr">{String(row.address).padStart(3, '0')}</span>
              <span className="patch-dip">
                {t('games.ld.dip')}: {row.dip.length > 0 ? row.dip.join(', ') : '—'}
              </span>
              {row.overflow ? (
                <span className="patch-warn" title={t('games.ld.overflowHint')}>
                  {t('games.ld.overflow')}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
        <p className="tool-hint">{t('games.ld.patchHint')}</p>
      </div>

      {/* ------------------------------------------------ Gélatines */}
      <div className="tool-card">
        <div className="tool-card-head">{t('games.ld.gelTitle')}</div>

        <label className="tool-field tool-field-wide">
          <span>{t('games.ld.search')}</span>
          <input
            type="search"
            value={query}
            data-ld-gel-search
            placeholder={t('games.ld.searchPh')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>

        <div className="gel-grid">
          {filtered.map((gel) => (
            <button
              key={gel.code}
              type="button"
              className={`gel-chip${selected.code === gel.code ? ' on' : ''}`}
              data-gel={gel.code}
              aria-pressed={selected.code === gel.code}
              onClick={() => setSelected(gel)}
            >
              <span className="gel-swatch" style={{ background: gel.hex }} aria-hidden="true" />
              <span className="gel-code">{gel.code}</span>
              <span className="gel-name">{gel.name}</span>
            </button>
          ))}
        </div>

        <div className="gel-detail" data-gel-detail>
          <span className="gel-preview" style={{ background: selected.hex }} aria-hidden="true" />
          <div className="gel-codes">
            <b data-gel-name>{selected.code} · {selected.name}</b>
            <span>{t('games.ld.rgb')}: {codes.rgb}</span>
            <span>{t('games.ld.cmy')}: {codes.cmy}</span>
            <span>{t('games.ld.hex')}: {codes.hex}</span>
            <span>{t('games.ld.kelvin')}: {codes.kelvin} K</span>
          </div>
        </div>
        <p className="tool-hint">{t('games.ld.gelHint')}</p>
      </div>

      {/* ------------------------------------------------ Faisceau */}
      <div className="tool-card">
        <div className="tool-card-head">{t('games.ld.beamTitle')}</div>

        <div className="tool-row">
          <label className="tool-field tool-field-wide">
            <span>{t('games.ld.distance')}</span>
            <input
              type="range"
              min={1}
              max={40}
              step={0.5}
              value={distance}
              data-ld-beam-distance
              onChange={(event) => setDistance(Number(event.target.value))}
            />
            <b data-ld-beam-distance-value>{distance.toFixed(1)} m</b>
          </label>
          <label className="tool-field tool-field-wide">
            <span>{t('games.ld.angle')}</span>
            <input
              type="range"
              min={5}
              max={60}
              step={1}
              value={angle}
              data-ld-beam-angle
              onChange={(event) => setAngle(Number(event.target.value))}
            />
            <b data-ld-beam-angle-value>{angle}°</b>
          </label>
        </div>

        <div className="beam-readout">
          <span className="beam-value" data-ld-beam-diameter>
            ⌀ {diameter.toFixed(2)} m
          </span>
          <span className="beam-meta">
            {t('games.ld.coverage')}: {(diameter * 100).toFixed(0)} cm · {t('games.ld.ratio')}:{' '}
            {throwRatio.toFixed(3)}
          </span>
        </div>

        {/* Schéma : le cône est dessiné en CSS (deux bords qui divergent). */}
        <div className="beam-diagram" aria-hidden="true">
          <span className="beam-source" />
          <span
            className="beam-cone"
            style={{ width: `${clamp((diameter / 12) * 100, 8, 100)}%`, transform: `scaleY(1)` }}
          />
          <span className="beam-floor" />
        </div>

        <p className="tool-hint">{t('games.ld.beamHint')}</p>
      </div>

      {/* ------------------------------------------------ Puissance */}
      <div className="tool-card">
        <div className="tool-card-head">{t('games.ld.powerTitle')}</div>

        <div className="tool-row">
          <label className="tool-field">
            <span>{t('games.ld.watts')}</span>
            <input
              type="number"
              min={0}
              step={100}
              value={watts}
              data-ld-power-watts
              onChange={(event) => setWatts(Number(event.target.value))}
            />
          </label>
          <label className="tool-field">
            <span>{t('games.ld.voltage')}</span>
            <select value={voltage} data-ld-power-voltage onChange={(event) => setVoltage(Number(event.target.value))}>
              <option value={230}>230 V (EU)</option>
              <option value={120}>120 V (US)</option>
            </select>
          </label>
          <label className="tool-field">
            <span>{t('games.ld.powerFactor')}</span>
            <input
              type="number"
              min={0.1}
              max={1}
              step={0.05}
              value={pf}
              data-ld-power-pf
              onChange={(event) => setPf(Number(event.target.value))}
            />
          </label>
        </div>

        <div className="power-readout">
          <div>
            <span className="power-label">{t('games.ld.totalCurrent')}</span>
            <b data-ld-power-total>{load.totalAmps.toFixed(1)} A</b>
          </div>
          <div>
            <span className="power-label">{t('games.ld.perPhase')}</span>
            <b data-ld-power-phase>{load.perPhase.toFixed(1)} A</b>
          </div>
          <div>
            <span className="power-label">{t('games.ld.apparent')}</span>
            <b>{load.kva.toFixed(1)} kVA</b>
          </div>
          <div>
            <span className="power-label">{t('games.ld.connector')}</span>
            <b data-ld-power-connector>{load.connector}</b>
          </div>
        </div>

        <p className="tool-hint">{t('games.ld.powerHint')}</p>
      </div>
    </section>
  );
}
